/**
 * Whether this computer is genuinely ready to work disconnected, and if not,
 * what to do about it.
 *
 * Derived from what is in the database, every time it is asked. There is no
 * "bootstrap finished" flag anywhere in this file for a reason: a flag records
 * that a process ended, not that the data arrived, and the gap between those two
 * is exactly the failure that stays invisible until the network goes. If the
 * schemas are missing, this says so, however cleanly the run reported.
 *
 * The reasons are ordered by what an operator should do first, and each one is
 * phrased as an action rather than a diagnosis. At three in the morning "Ask IT
 * to allow this site to store data" is worth more than "storage not persisted".
 */
import { effectiveNow } from "../clock"
import { offlineDatabase, readDeviceState, type OfflineDatabase } from "../../db/schema"
import { verifySnapshot } from "../snapshot/verify"
import type { StorageState } from "./storage"

export type ReadinessCode =
  /**
   * Not raised here. `db/open.ts` decides it, before this file has a database to
   * read, and it is in the union so the heartbeat and the readiness page can
   * carry it in the same field as every other reason (§22).
   */
  | "migration_refused"
  | "not_enrolled"
  | "credential_expired"
  | "credential_expiring_soon"
  | "storage_not_persisted"
  | "bootstrap_incomplete"
  | "quota_exhausted"
  | "snapshot_missing"
  | "snapshot_invalid"
  | "snapshot_expired"
  | "snapshot_expiring_soon"
  | "schemas_missing"
  | "station_configuration_missing"
  | "clock_never_measured"
  | "clock_regression"
  | "clock_drift"
  | "quota_low"

export interface ReadinessCheck {
  code: ReadinessCode
  /** False means this alone stops the computer being offline-ready. */
  blocking: boolean
  action: string
}

export interface ReadinessResult {
  ready: boolean
  /** True when the device may read offline but not write — an expired snapshot. */
  readOnly: boolean
  checks: ReadinessCheck[]
  /** The first blocking reason, for the heartbeat's one-line field. */
  reason: ReadinessCode | null
  entryCount: number
  registerCount: number
}

export interface ReadinessContext {
  enrolled: boolean
  credentialExpiresAt: string | null
  /** How many registers the station has, from the last manifest. */
  expectedRegisterCount: number | null
  /**
   * How much room is left, when the caller wants that checked.
   *
   * Injected rather than imported so this file goes on deriving everything from
   * the database and nothing from the browser — which is what lets it be tested
   * without one, and what lets the register page skip a `navigator.storage`
   * round trip it has no use for. `storage.ts`'s `storageState` is what a caller
   * passes; omitting it omits the check.
   */
  storage?: () => Promise<StorageState>
}

const CREDENTIAL_WARNING_MS = 7 * 24 * 60 * 60 * 1000
const SNAPSHOT_WARNING_MS = 7 * 24 * 60 * 60 * 1000

/**
 * How far this computer's clock may sit from the server's before it is worth
 * saying so (§20.3).
 *
 * Two minutes, and not tighter. The offline login code's own window is minutes
 * wide, the ratchet in `clock.ts` already makes expiry decisions safe against a
 * wrong clock, and the thing this actually protects is the *recorded* time on
 * entries — a caution order filed ninety seconds out is filed correctly for
 * every operational purpose. A threshold at ten seconds would light on every
 * station computer that has not seen an NTP server this week, and a warning
 * that is always lit is a warning nobody reads.
 *
 * Not blocking. A drifting clock is something to correct this week, and refusing
 * to let a station work offline over it would be a far larger harm than the one
 * it prevents. `clock_regression` — a clock that has gone *backwards* — is the
 * blocking one, and stays so.
 */
export const CLOCK_DRIFT_WARNING_MS = 2 * 60 * 1000

/**
 * When a full disk stops being a distant possibility (§20.1's "warn well before
 * quota pressure forces any decision").
 *
 * Expressed as a fraction rather than a byte count because quotas differ by an
 * order of magnitude between browsers and machines, and as *used* rather than
 * *free* because that is what the estimate reports directly.
 */
export const QUOTA_WARNING_FRACTION = 0.8

export async function evaluateReadiness(
  context: ReadinessContext,
  db: OfflineDatabase = offlineDatabase(),
  // Injectable for the same reason the verifier's is: every judgement below is a
  // time comparison, and a test that cannot place the machine's clock cannot
  // exercise any of them.
  wallClockMs: number = Date.now()
): Promise<ReadinessResult> {
  const checks: ReadinessCheck[] = []
  const { nowMs, regressionDetected, sinceTrustedMs } = await effectiveNow(db, wallClockMs)
  const state = await readDeviceState(db)

  if (!context.enrolled) {
    checks.push({
      code: "not_enrolled",
      blocking: true,
      action: "This computer has not been enrolled. Ask an administrator to nominate it."
    })
  }

  if (context.credentialExpiresAt) {
    const expiresAt = Date.parse(context.credentialExpiresAt)
    if (expiresAt <= nowMs) {
      checks.push({
        code: "credential_expired",
        blocking: true,
        action: "This computer's credential has expired. Connect it to the network to renew it."
      })
    } else if (expiresAt - nowMs < CREDENTIAL_WARNING_MS) {
      checks.push({
        code: "credential_expiring_soon",
        blocking: false,
        action: "Connect this computer to the network soon to renew its credential."
      })
    }
  }

  // Blocking rather than advisory. Without a persistence grant the browser may
  // evict everything below under disk pressure, and eviction leaves no trace —
  // the computer simply comes back empty, during the outage.
  if (state.storagePersisted !== true) {
    checks.push({
      code: "storage_not_persisted",
      blocking: true,
      action: "This browser has not been allowed to keep data. Ask IT to permit persistent storage for this site."
    })
  }

  const snapshotChecks = await evaluateSnapshot(nowMs, db)
  checks.push(...snapshotChecks)

  const registerCount = await db.registerSchemas.count()
  const entryCount = await db.registerEntries.count()

  if (registerCount === 0) {
    checks.push({
      code: "schemas_missing",
      blocking: true,
      action: "No register schemas are stored. Run the readiness bootstrap while connected."
    })
  } else if (context.expectedRegisterCount !== null && registerCount < context.expectedRegisterCount) {
    checks.push({
      code: "schemas_missing",
      blocking: true,
      action: `Only ${registerCount} of ${context.expectedRegisterCount} register schemas are stored. Run the bootstrap again while connected.`
    })
  }

  if ((await db.stationConfiguration.count()) === 0) {
    checks.push({
      code: "station_configuration_missing",
      blocking: true,
      action: "This station's configuration is not stored. Run the readiness bootstrap while connected."
    })
  }

  const failedSteps = await incompleteSteps(db)
  if (failedSteps.includes("quota_exceeded")) {
    checks.push({
      code: "quota_exhausted",
      blocking: true,
      action: "This computer ran out of storage part-way through. Free up disk space, then resume the bootstrap."
    })
  } else if (failedSteps.length > 0) {
    checks.push({
      code: "bootstrap_incomplete",
      blocking: true,
      action: `The readiness bootstrap did not finish (${failedSteps.length} step(s) outstanding). Resume it while connected.`
    })
  }

  if (sinceTrustedMs === null) {
    checks.push({
      code: "clock_never_measured",
      blocking: true,
      action: "This computer has never reached the server, so its clock cannot be trusted. Connect it once."
    })
  }

  if (regressionDetected) {
    checks.push({
      code: "clock_regression",
      blocking: true,
      action: "This computer's clock has gone backwards. Correct the date and time, then connect it to the network."
    })
  } else if (state.clockOffsetMs !== null && Math.abs(state.clockOffsetMs) > CLOCK_DRIFT_WARNING_MS) {
    // Only when the clock has not also gone backwards. A machine with both
    // faults has one problem, and the regression is the half worth saying.
    checks.push({
      code: "clock_drift",
      blocking: false,
      action:
        `This computer's clock is about ${describeDrift(state.clockOffsetMs)} the server's. ` +
        "Correct the date and time so entries are filed at the time they happened."
    })
  }

  const storage = await context.storage?.()
  if (storage?.supported && storage.quotaBytes !== null && storage.usageBytes !== null) {
    if (storage.usageBytes / storage.quotaBytes > QUOTA_WARNING_FRACTION) {
      // Not blocking, and deliberately: the computer works, and it works for a
      // while yet. §20.1 asks to warn *well before* the decision is forced,
      // which is a different thing from refusing at the threshold.
      checks.push({
        code: "quota_low",
        blocking: false,
        action:
          `This computer has used ${Math.round((storage.usageBytes / storage.quotaBytes) * 100)}% of the ` +
          "storage it is allowed. Ask IT to free disk space before it runs out during an outage."
      })
    }
  }

  const blocking = checks.filter(check => check.blocking)
  const readOnly = blocking.every(check => check.code === "snapshot_expired") && blocking.length > 0

  return {
    ready: blocking.length === 0,
    readOnly,
    checks,
    reason: blocking[0]?.code ?? null,
    entryCount,
    registerCount
  }
}

async function evaluateSnapshot(nowMs: number, db: OfflineDatabase): Promise<ReadinessCheck[]> {
  const stored = await db.snapshot.toCollection().first()

  if (!stored) {
    return [ {
      code: "snapshot_missing",
      blocking: true,
      action: "No authorization snapshot is stored. Run the readiness bootstrap while connected."
    } ]
  }

  // Re-verified on every evaluation rather than trusted because it was verified
  // once at bootstrap. The bytes have been sitting in a database an operator can
  // reach; checking them again costs one signature verification.
  const verification = await verifySnapshot(stored)
  if (!verification.valid) {
    return [ {
      code: "snapshot_invalid",
      blocking: true,
      action: "The stored authorization snapshot did not verify. Run the readiness bootstrap while connected."
    } ]
  }

  const expiresAt = Date.parse(stored.expiresAt)

  if (expiresAt <= nowMs) {
    return [ {
      code: "snapshot_expired",
      blocking: true,
      action: "The authorization snapshot has expired. This computer is read-only until it reconnects."
    } ]
  }

  if (expiresAt - nowMs < SNAPSHOT_WARNING_MS) {
    return [ {
      code: "snapshot_expiring_soon",
      blocking: false,
      action: "The authorization snapshot expires soon. Connect this computer to the network to renew it."
    } ]
  }

  return []
}

/** "two minutes ahead of" / "five minutes behind", which is how a person says it. */
function describeDrift(offsetMs: number): string {
  const minutes = Math.round(Math.abs(offsetMs) / 60_000)
  const magnitude = minutes < 1 ? "less than a minute" : `${minutes} minute${minutes === 1 ? "" : "s"}`

  // `clockOffsetMs` is the wall clock minus the server's, so positive is fast.
  return `${magnitude} ${offsetMs > 0 ? "ahead of" : "behind"}`
}

async function incompleteSteps(db: OfflineDatabase): Promise<string[]> {
  const state = await db.syncState.get("self")
  if (!state) return [ "never_run" ]

  return Object.entries(state.bootstrapProgress)
    .filter(([ , record ]) => record.status !== "done")
    .map(([ key, record ]) => (record.error === "quota_exceeded" ? "quota_exceeded" : key))
}
